import { Text } from "../UI";
import errorImg from "../assets/img/warning-2.svg";

import CloseImg from "../assets/img/close-circle.svg";

import successImg from "../assets/img/tick-square.svg";

import { createContext, useContext, useEffect, useState } from "react";
import styled, { useTheme } from "styled-components";
import { useMobile } from "../utils/mobile";

const Circle = styled("div")(({ bgColor }) => ({
  display: "flex",
  width: 32,
  height: 32,
  padding: 8,
  justifyContent: "center",
  alignItems: "center",
  gap: 10,
  borderRadius: 20,
  backgroundColor: bgColor,
}));

const Container = styled("div")(
  ({ bgColor, color, borderColor, isMobile, isLogin, buttonPosition }) => {
    const baseStyles = {
      width: isMobile ? 362 : 462,
      minHeight: 54,
      display: "flex",
      flexDirection: "row",
      backgroundColor: bgColor,
      color: color,
      gap: 10,
      padding: isMobile ? "10px 14px" : "10px 20px",
      alignItems: "flex-start",
      zIndex: 99999,
      borderRadius: 18,
      justifyContent: "center",
      border: `1px solid ${borderColor}`,
    };

    // If buttonPosition is provided, center relative to button
    if (buttonPosition) {
      const notificationWidth = isMobile ? 362 : 462;
      const buttonCenterX = buttonPosition.left + (buttonPosition.width / 2);
      return {
        ...baseStyles,
        position: "fixed", // Use fixed instead of absolute for viewport-relative positioning
        left: buttonCenterX, // Position at button center
        top: buttonPosition.top + buttonPosition.height + 20, // Position below button with 20px spacing
        margin: 0,
        transform: "translateX(-50%)", // Center horizontally by moving left by 50% of own width
      };
    }

    // Default positioning (fallback)
    return {
      ...baseStyles,
      position: "absolute",
      bottom: isLogin ? 80 : 10,
      left: 0,
      right: isMobile ? 0 : isLogin ? -850 : 250,
      margin: "0 auto",
      marginBottom: 30,
    };
  }
);

export const MessageType = {
  ERROR: "error",
  SUCCESS: "success",
};

const getImgNotificationByType = (type) => {
  switch (type) {
    case MessageType.ERROR:
      return errorImg;

    case MessageType.SUCCESS:
      return successImg;

    default:
      return undefined;
  }
};

const getCircleColorByType = (theme, type) => {
  switch (type) {
    case MessageType.ERROR:
      return "#F37153";

    case MessageType.SUCCESS:
      return "#4BBC00";

    default:
      return undefined;
  }
};

const getColorNotificationByType = (theme, type) => {
  switch (type) {
    case MessageType.ERROR:
      return theme.color.error600;

    case MessageType.SUCCESS:
      return theme.color.success700;

    default:
      return undefined;
  }
};

const getBgColorNotificationByType = (theme, type) => {
  switch (type) {
    case MessageType.ERROR:
      return theme.color.error100;

    case MessageType.INFO:
      return "#EEFFE2";

    case MessageType.SUCCESS:
      return theme.color.success50;

    case MessageType.WARNING:
      return "#FFF7E9";

    case MessageType.GENERAL:
      return theme.color.gray800;

    default:
      return undefined;
  }
};

const getBorderColorNotificationByType = (theme, type) => {
  switch (type) {
    case MessageType.ERROR:
      return "#FFA995;";

    case MessageType.SUCCESS:
      return "#9ADF6B";

    default:
      return undefined;
  }
};

const MessageNotification = ({ text, onClose, type, isLogin, buttonPosition }) => {
  const theme = useTheme();
  const isMobile = useMobile();

  const [cleanText, setCleanText] = useState("");

  useEffect(() => {
    const cleanned = text.replace(/n\\|\\| n |\r?\n/g, " ");
    setCleanText(cleanned);
  }, [text]);

  useEffect(() => {
    const timeId = setTimeout(() => {
      // After 5 seconds set the show value to false
      onClose();
    }, 5000);

    return () => {
      clearTimeout(timeId);
    };
  }, []);

  return (
    <Container
      color={getColorNotificationByType(theme, type)}
      bgColor={getBgColorNotificationByType(theme, type)}
      borderColor={getBorderColorNotificationByType(theme, type)}
      isMobile={isMobile}
      isLogin={isLogin}
      buttonPosition={buttonPosition}
    >
      <Circle bgColor={getCircleColorByType(theme, type)}>
        <img alt="message_img" src={getImgNotificationByType(type)} />
      </Circle>
      <Text
        color={getColorNotificationByType(theme, type)}
        style={{ textAlign: "start" }}
      >
        {cleanText}
      </Text>

      <img
        onClick={() => {
          onClose();
        }}
        style={{ marginInlineStart: "auto" }}
        alt="close_msg"
        src={CloseImg}
      />
    </Container>
  );
};

const MessageNotificationContext = createContext({
  showMessageNotification: false,
  setMessageNotification: () => {},
});

const MessageNotificationProvider = ({ children }) => {
  const [messageNotification, setMessageNotification] = useState({
    show: false,
    text: "",
    type: MessageType.ERROR,
  });

  return (
    <MessageNotificationContext.Provider
      value={{ messageNotification, setMessageNotification }}
    >
      {children}
    </MessageNotificationContext.Provider>
  );
};

const useMessageNotification = () => {
  return useContext(MessageNotificationContext);
};

export default MessageNotification;
export { MessageNotificationProvider, useMessageNotification };
